import React, { ReactNode, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useMedia } from 'react-use';

import {
  ALLOWED_MIME_TYPES,
  DOCUMENT_TYPES,
  IMAGE_DOCUMENT_TYPES,
  media,
  MIME_TYPES,
} from '@savgroup-front-common/constants';
import { ExtendedFile, MessageType } from '@savgroup-front-common/types';

import { CommonAttachmentService } from '../../../api';
import { SessionType } from '../../../api/Attachment/getFilesUploadLinksBySessionIdCommand';
import { MultiFileUploadHookForm } from '../../../atoms/Form';
import { FieldMessages } from '../../../atoms/Form/common/helpers/getFinalFieldState.types';
import { NewDesignMultiFileUploadHookForm } from '../../../atoms/Form/NewDesignMultiFileUploadHookForm';
import { toast } from '../../../atoms/Toast';
import { safeFormattedIntlString } from '../../../formatters';
import { delay } from '../../../helpers';
import { useToasts } from '../../../molecules/NotificationsProvider';
import { QrCodeImportModal } from '../components/QrCodeImportModal/QrCodeImportModal';

import messages from './messages';

interface AdditionalInformationMultiFileInputProps {
  name: string;
  label?: MessageType | string;
  postLabel: string | ReactNode;
  value?: { value: ExtendedFile }[];
  onChange: (files?: File[]) => void;
  onRemove?: (file: ExtendedFile, index: number) => void;
  isRequired: boolean;
  errors: FieldMessages;
  isLiveUpload?: boolean;
  isImportByQrCode: boolean;
  additionalInformationId: string;
  dataTestId: string;
  claimIds: string[];
  documentType?: DOCUMENT_TYPES;
  isNewDesign?: boolean;
}

export const AdditionalInformationMultiFileInput: React.FC<
  AdditionalInformationMultiFileInputProps
> = ({
  name,
  label,
  postLabel,
  value = [],
  onChange,
  onRemove,
  isRequired,
  errors,
  isLiveUpload,
  isImportByQrCode,
  additionalInformationId,
  dataTestId,
  claimIds,
  documentType,
  isNewDesign = false,
}) => {
  const isMobileView = useMedia(media.maxWidth.xs);
  const formContext = useFormContext();
  const { setValue, getValues } = formContext;

  const { pushError } = useToasts();
  const { fileId } = useParams<{ fileId?: string }>();

  const [isOpen, setIsOpen] = useState(false);

  const { mutateAsync: handleUploadFinishClick, isLoading } = useMutation(
    ['syncrhonizeFileAdditionalInformationForm'],
    async ({ sessionId }: { sessionId?: string }) => {
      if (!sessionId) {
        setIsOpen(false);

        return undefined;
      }

      await delay(500);

      const currentFiles = getValues(name) ?? [];

      const response =
        await CommonAttachmentService.getFilesDownloadLinksBySessionIdQuery({
          sessionId,
        });

      if (response.failure) {
        pushError(
          new Error(safeFormattedIntlString(messages.errorProcessingFile)),
        );

        setIsOpen(false);

        return undefined;
      }

      if (!response.value?.links?.length) {
        setIsOpen(false);

        return undefined;
      }

      const files = (
        await Promise.all(
          response.value.links.map(async (link) => {
            const response = await fetch(link);
            const fileName = decodeURI(
              response.headers.get('x-ms-meta-originalname') ?? name,
            );
            const blob = await response.blob();

            const file = new File([blob], fileName, {
              type: blob.type,
            });

            if ('failure' in file) {
              pushError(
                new Error(
                  safeFormattedIntlString(messages.errorProcessingFile),
                ),
              );

              return undefined;
            }

            return file;
          }),
        )
      ).filter((x) => !!x);

      if (fileId) {
        if (files) {
          setValue(name, [
            ...currentFiles,
            ...files.map((file) => ({ value: file })),
          ]);

          toast.success(
            safeFormattedIntlString(messages.fileUploadedSuccessfully),
          );
        }
        setIsOpen(false);

        return;
      }

      if (files) {
        setValue(name, [
          ...currentFiles,
          ...files.map((file) => ({ value: file })),
        ]);

        toast.success(
          safeFormattedIntlString(messages.fileUploadedSuccessfully),
        );
      }

      setIsOpen(false);
    },
  );

  const allowedMimeTypes = Object.values(IMAGE_DOCUMENT_TYPES).includes(
    documentType as DOCUMENT_TYPES,
  )
    ? [MIME_TYPES.JPEG, MIME_TYPES.PNG, MIME_TYPES.IMAGE_HEIC, MIME_TYPES.PDF]
    : ALLOWED_MIME_TYPES;

  return (
    <>
      {isImportByQrCode && !isMobileView && claimIds[0] && (
        <QrCodeImportModal
          isOpen={isOpen}
          additionalInformationId={additionalInformationId}
          claimId={claimIds[0]}
          onUploadFinishClick={handleUploadFinishClick}
          isSubmitLoading={isLoading}
          sessionType={SessionType.MULTI_FILES}
        />
      )}

      {isNewDesign && (
        <NewDesignMultiFileUploadHookForm
          allowedMimeTypes={allowedMimeTypes}
          name={name}
          label={label}
          postLabel={postLabel}
          onSelect={onChange}
          onRemove={onRemove}
          files={value?.map((value) => value.value) ?? []}
          isRequired={isRequired}
          errors={errors}
          isLiveUpload={isLiveUpload}
          onImportByQrCodeClick={
            isImportByQrCode && !isMobileView
              ? () => {
                  setIsOpen(true);
                }
              : undefined
          }
          dataTestId={dataTestId}
          isFullWidth
        />
      )}

      {!isNewDesign && (
        <MultiFileUploadHookForm
          allowedMimeTypes={allowedMimeTypes}
          hollow
          name={name}
          label={label}
          postLabel={postLabel}
          onSelect={onChange}
          onRemove={onRemove}
          files={value?.map((value) => value.value) ?? []}
          isRequired={isRequired}
          errors={errors}
          isLiveUpload={isLiveUpload}
          isFullWidth
          onImportByQrCodeClick={
            isImportByQrCode && !isMobileView
              ? () => {
                  setIsOpen(true);
                }
              : undefined
          }
          dataTestId={dataTestId}
        />
      )}
    </>
  );
};
